import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { CheckCircle2, Printer, Settings } from 'lucide-react'
import { Button } from '@/app/components/ui/Button'
import { Stepper, type StepDef } from '@/app/components/ui/Stepper'
import { SelectStep } from '@/app/screens/flow/SelectStep'
import { LayoutStep } from '@/app/screens/flow/LayoutStep'
import { PrintStep } from '@/app/screens/flow/PrintStep'
import { publishToGuest } from '@/app/lib/guestChannel'
import { onQueueEvent } from '@/app/lib/local/queue'
import { printSheet } from '@/app/lib/print'
import { renderQrDataUrl } from '@/app/lib/qr'
import { toast } from 'sonner'
import { shareUrlFor } from '@/core/session'
import { useAppStore } from '@/app/store/useAppStore'
import { useFlowStore } from '@/app/store/useFlowStore'

const FRONTEND_BASE = import.meta.env.VITE_FRONTEND_BASE ?? 'https://raiaphoto.tech'

const STEPS: StepDef[] = [
  { id: 'select', label: 'Pilih' },
  { id: 'layout', label: 'Layout' },
  { id: 'print', label: 'Cetak' },
]

/**
 * The guided flow: one decision per screen, with the rail across the top
 * showing where the operator is.
 *
 * The desktop app put everything on one dense window and hid the ordering in
 * button visibility — buttons appeared and disappeared as state changed, and
 * several were unreachable entirely. Splitting it into three steps means there
 * is never more than one thing to decide, which is what makes it usable by
 * someone who was handed the booth ten minutes ago.
 */
export function FlowScreen() {
  const navigate = useNavigate()
  const { step, setStep, sessionKey, printing, reset, beginSession } = useFlowStore()
  const [finished, setFinished] = useState<string | null>(null)

  // Keyboard is layered on top of touch, never required: Esc goes back a step.
  // Ignored while a print is in flight, and while a field has focus.
  // (Enter is the Button component's own shortcut, not handled here.)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (printing) return
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return

      if (event.key === 'Escape' && step > 0) {
        event.preventDefault()
        setStep(step - 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [step, printing, setStep])

  // The finish screen is laid OVER the flow rather than returned in place of it.
  // An early return here unmounted the gallery after every single guest, so
  // "Tamu berikutnya" landed on a grid being rebuilt from nothing — see below.
  const showFinished = Boolean(finished)

  return (
    <>
      {showFinished && (
        <Finished
          shareUrl={finished ?? ''}
          sessionKey={sessionKey}
          onNext={() => {
            setFinished(null)
            publishToGuest({ type: 'qr.hide' })
            reset()
          }}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col" hidden={showFinished}>
        <div className="flex shrink-0 items-center gap-3 px-5 pt-4 sm:px-8">
          <Stepper steps={STEPS} current={step} onGoTo={printing ? undefined : setStep} />
          <div className="flex-1" />
          <Button
            variant="ghost"
            aria-label="Pengaturan"
            onClick={() => navigate('/settings')}
            disabled={printing}
            className="w-[48px] px-0"
          >
            <Settings size={20} />
          </Button>
        </div>

        {/*
          The session key is minted HERE, on the way out of step 1, not inside the
          layout step's effect — that effect depends on the key, so minting it
          there re-triggered the effect and composited everything twice per guest.
          Leaving step 1 is also the honest moment: it is where the operator
          commits to a selection, and the QR is derived from the key.
        */}
        {/*
          Step 1 stays MOUNTED for the life of the flow and is hidden, not removed.

          Unmounting it cost a full rebuild on every return: all tiles re-created,
          each one opening its file again before it could even look up its cached
          thumbnail, plus a fresh poll of the whole folder. Measured on a
          120-photo folder, coming back took ~0.5s of blank tiles and 255 file
          opens — and it scales with the folder, which on an event floor is
          hundreds of frames. Hidden, it costs nothing, and the scroll position
          and zoom the operator left it at are still there.

          Steps 2 and 3 stay conditional: they are per-session work that has to
          start fresh, and neither is the screen an operator returns to all night.
        */}
        <div className="flex min-h-0 flex-1 flex-col" hidden={step !== 0}>
          <SelectStep
            active={step === 0 && !showFinished}
            onNext={() => {
              beginSession()
              setStep(1)
            }}
          />
        </div>
        {step === 1 && <LayoutStep onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        {step === 2 && (
          <PrintStep
            onBack={() => setStep(1)}
            onDone={() => setFinished(sessionKey ? shareUrlFor(sessionKey, FRONTEND_BASE) : '')}
          />
        )}
      </div>
    </>
  )
}

/** How long the finish screen waits before returning itself to step 1. */
const AUTO_NEXT_SECONDS = 30

/**
 * The hand-off moment: the guest's code, and the way back to a ready console.
 *
 * Three things were added after watching the flow end to end.
 *
 * The countdown is **shown**. It always returned itself to step 1 after 20
 * silent seconds — right for an operator who is looking at the guest rather
 * than the screen, wrong when the screen changes under someone mid-sentence.
 * It is also cancellable now, because the one moment you want it to stop is
 * while a guest is asking what the link was.
 *
 * **Cetak ulang** exists because a browser print dialog can be cancelled, and
 * the sheet can come out on the wrong paper. Before this, the composite was
 * discarded on the way back and the only recovery was to reselect every photo
 * and rebuild the session — with a queue behind you. The composite is still in
 * the flow store until "Tamu berikutnya", so reprinting is free.
 *
 * The **upload state** is here because the operator is about to tell a guest to
 * scan a code, and this is the last moment they can see whether the session
 * that code points at has actually gone anywhere.
 */
function Finished({
  shareUrl,
  sessionKey,
  onNext,
}: {
  shareUrl: string
  sessionKey: string | null
  onNext: () => void
}) {
  const settings = useAppStore((s) => s.settings)
  const uploads = useAppStore((s) => s.uploads)
  const { rendered, chosenLayoutId, copies } = useFlowStore()

  const [remaining, setRemaining] = useState(AUTO_NEXT_SECONDS)
  const [holding, setHolding] = useState(false)
  const [reprinting, setReprinting] = useState(false)
  const [uploaded, setUploaded] = useState(false)
  const [qr, setQr] = useState<string | null>(null)

  // The same code that is on the sheet and on the guest screen. Here because
  // the guest screen is opened deliberately and may not be — with no second
  // monitor this is the only place a guest can scan from, and it saves turning
  // the operator's screen around to read a URL out loud.
  useEffect(() => {
    if (!shareUrl) return
    let cancelled = false
    void renderQrDataUrl(shareUrl, 320).then((dataUrl) => {
      if (!cancelled) setQr(dataUrl)
    })
    return () => {
      cancelled = true
    }
  }, [shareUrl])

  /**
   * Leaves the finish screen exactly once, however it is triggered.
   *
   * The countdown used to call `onNext` from an effect that listed `onNext` as
   * a dependency, and it looped. `onNext` is an inline arrow, so every
   * FlowScreen render handed this screen a new one. When the countdown reached
   * 0 it ran `reset()` — a zustand update, which React applies synchronously
   * and AHEAD of the `setFinished(null)` beside it — so FlowScreen re-rendered
   * with the finish screen still up, passed a fresh `onNext`, the effect saw a
   * changed dependency with `remaining` still 0, and called it again. React
   * stopped it at 50 with "Maximum update depth exceeded" and replaced the whole
   * console with an error page: on any guest the operator walked away from.
   * Pressing "Tamu berikutnya" never looped, because a click applies both
   * updates in one render — which is why it only ever showed up unattended.
   *
   * So: the callback is read through a ref and is not a dependency, and a
   * one-shot guard makes a second call a no-op, so a countdown landing on 0 as
   * the operator taps the button cannot advance twice either.
   */
  const onNextRef = useRef(onNext)
  useEffect(() => {
    onNextRef.current = onNext
  })
  const advanced = useRef(false)
  const advance = () => {
    if (advanced.current) return
    advanced.current = true
    onNextRef.current()
  }

  useEffect(() => {
    if (holding) return
    if (remaining <= 0) {
      advance()
      return
    }
    const timer = setTimeout(() => setRemaining((value) => value - 1), 1000)
    return () => clearTimeout(timer)
    // `advance` reads everything through refs; listing it would re-run this
    // on every render, which is precisely the loop described above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, holding])

  // The queue drops a job when it finishes, so "gone from the queue and never
  // reported failing" is what success looks like from here.
  const progress = uploads.find((upload) => upload.sessionKey === sessionKey)
  useEffect(() => {
    if (!sessionKey) return
    return onQueueEvent((event) => {
      if (event.sessionKey !== sessionKey) return
      if (event.type === 'upload.done') setUploaded(true)
    })
  }, [sessionKey])

  const chosen = rendered?.find((item) => item.layout.id === chosenLayoutId)

  const reprint = async () => {
    if (!chosen || !settings) return
    setHolding(true)
    setReprinting(true)
    try {
      await printSheet({
        image: chosen.result.print,
        orientation: chosen.result.width >= chosen.result.height ? 'Landscape' : 'Portrait',
        invertOrientation: settings.invertOrientation,
        inset: { x: settings.printInsetX, y: settings.printInsetY },
        copies,
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal mencetak ulang')
    } finally {
      setReprinting(false)
    }
  }

  return (
    <div className="citaria-step-enter grid flex-1 place-items-center px-6 text-center">
      <div className="w-full max-w-[520px]">
        <CheckCircle2 size={56} className="mx-auto mb-4 text-ok" />
        <h1 className="text-[30px] font-semibold tracking-tight">Selesai</h1>
        <p className="mt-2 text-[16px] text-ink-muted">
          Foto sedang dicetak. Tamu bisa memindai QR di bawah.
        </p>

        {/*
          On white with a quiet zone, for the same reason the guest screen is:
          a code on a coloured ground scans measurably worse from a phone held
          at an angle under venue lighting, which is the only way this is read.
        */}
        {qr && (
          <div className="mx-auto mt-5 w-fit rounded-[16px] bg-white p-3 shadow-xl">
            <img
              src={qr}
              alt="Kode QR untuk mengunduh foto"
              className="h-[200px] w-[200px] object-contain"
            />
          </div>
        )}

        {shareUrl && (
          <p className="mx-auto mt-4 break-all rounded-[12px] bg-surface px-4 py-3 font-mono text-[13px] text-ink-muted ring-1 ring-line">
            {shareUrl}
          </p>
        )}

        <p className="mt-3 text-[13px] text-ink-muted">
          {uploaded
            ? 'Sesi sudah terunggah — link tamu aktif.'
            : progress?.error
              ? `Belum terunggah: ${progress.error}`
              : progress
                ? `Mengunggah ${progress.done}/${progress.total} berkas…`
                : 'Menunggu unggahan. Biarkan konsol terbuka.'}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button size="xl" className="min-w-[240px]" onClick={advance}>
            Tamu berikutnya
          </Button>
          <Button variant="secondary" size="lg" onClick={reprint} disabled={!chosen || reprinting}>
            <Printer size={18} />
            {reprinting ? 'Mencetak…' : 'Cetak ulang'}
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setHolding((value) => !value)}
          className="mt-5 min-h-[44px] text-[13px] text-ink-faint underline underline-offset-4"
        >
          {holding
            ? 'Hitung mundur dijeda — ketuk untuk lanjut'
            : `Lanjut sendiri dalam ${remaining} detik — ketuk untuk jeda`}
        </button>
      </div>
    </div>
  )
}
